import { useState } from "react";
import { ArrowLeft, Mail, User, ChevronDown, AlertTriangle, CheckCircle, Trash2, ShieldCheck, Clock } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

const REASONS = [
  "No longer using the app",
  "Privacy concerns",
  "Too many notifications",
  "Created a duplicate account",
  "Switching to a new account",
  "App did not meet my expectations",
  "Other",
];

export default function DeleteAccountPage() {
  const [form, setForm] = useState({ email: "", username: "", reason: "", details: "" });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState(false);
  const [alreadyExists, setAlreadyExists] = useState(false);

  const set = (k) => (e) => { setForm((f) => ({ ...f, [k]: e.target.value })); setError(""); };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.email.trim()) return setError("Please enter the email linked to your Fanible account.");
    if (!form.reason) return setError("Please select a reason for deletion.");
    setLoading(true);
    try {
      const res = await fetch(`${API}/api/delete-request`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(form),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || "Something went wrong. Please try again.");
      setSuccess(true);
      setAlreadyExists(!!data.already_exists);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#0C0E13]">
      {/* Header */}
      <div className="border-b border-white/5 bg-[#0A0C10]">
        <div className="max-w-3xl mx-auto px-6 md:px-10 py-5 flex items-center justify-between">
          <a href="/" className="flex items-center gap-2 text-[#A1A1AA] hover:text-white transition-colors text-sm font-medium group">
            <ArrowLeft size={15} className="group-hover:-translate-x-0.5 transition-transform" />
            Back to Fanible
          </a>
          <div className="flex items-center gap-2">
            <img src="/fanible-icon.png" alt="Fanible" className="w-6 h-6 rounded-lg" style={{ mixBlendMode: "screen" }} />
            <span className="font-outfit font-black text-white text-sm uppercase tracking-tight">Fanible</span>
          </div>
        </div>
      </div>

      <div className="max-w-2xl mx-auto px-6 md:px-10 py-14">

        {/* Title */}
        <div className="mb-10">
          <div className="inline-flex items-center gap-2 bg-red-500/10 border border-red-500/25 rounded-full px-3 py-1.5 mb-5">
            <Trash2 size={10} className="text-red-400" />
            <span className="text-[9px] font-black uppercase tracking-[0.2em] text-red-400">Account Deletion</span>
          </div>
          <h1 className="font-outfit font-black text-white text-4xl sm:text-5xl tracking-tight mb-4">
            Delete Your <span className="text-red-400">Account</span>
          </h1>
          <p className="text-[#A1A1AA] text-sm font-inter leading-relaxed">
            Submit a request to permanently delete your Fanible account and associated personal data.
            This page is publicly accessible and does not require a login.
          </p>
        </div>

        {!success ? (
          <>
            {/* What happens info */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-8">
              <div className="bg-white/[0.03] border border-white/8 rounded-2xl p-5">
                <div className="flex items-center gap-2 mb-3">
                  <Trash2 size={14} className="text-red-400" />
                  <span className="text-white font-semibold text-sm">What gets deleted</span>
                </div>
                <ul className="text-[#A1A1AA] text-xs font-inter space-y-1.5 list-disc pl-4 leading-relaxed">
                  <li>Your profile, username and photo</li>
                  <li>All posts, images and articles</li>
                  <li>Comments, votes and interactions</li>
                  <li>Follows and creator profile</li>
                  <li>Shoutout history and earnings access</li>
                </ul>
              </div>
              <div className="bg-white/[0.03] border border-white/8 rounded-2xl p-5">
                <div className="flex items-center gap-2 mb-3">
                  <ShieldCheck size={14} className="text-[#3B9EFF]" />
                  <span className="text-white font-semibold text-sm">What may be retained</span>
                </div>
                <ul className="text-[#A1A1AA] text-xs font-inter space-y-1.5 list-disc pl-4 leading-relaxed">
                  <li>Limited identifiers for fraud prevention</li>
                  <li>Records required by law, tax or accounting</li>
                  <li>Data needed for dispute resolution</li>
                </ul>
                <p className="text-[#A1A1AA] text-[10px] mt-3 leading-relaxed">
                  As described in our <a href="/privacy" className="text-[#3B9EFF] hover:underline">Privacy Policy</a> §16–18.
                </p>
              </div>
            </div>

            {/* Warning */}
            <div className="flex items-start gap-3 bg-[#f59e0b]/[0.06] border border-[#f59e0b]/20 rounded-2xl p-4 mb-8">
              <AlertTriangle size={16} className="text-[#f59e0b] flex-shrink-0 mt-0.5" />
              <p className="text-[#A1A1AA] text-xs font-inter leading-relaxed">
                <span className="text-white font-semibold">Deletion is permanent.</span> Your account, content,
                and any pending creator earnings cannot be recovered. Requests are verified and initiated
                within <span className="text-white font-semibold">24 hours</span> of successful verification.
              </p>
            </div>

            {/* Form */}
            <form onSubmit={submit} className="bg-white/[0.02] border border-white/8 rounded-2xl p-6 sm:p-8 space-y-4">

              <div>
                <label className="block text-[10px] font-bold uppercase tracking-widest text-[#A1A1AA] mb-2">
                  Account Email <span className="text-red-400">*</span>
                </label>
                <div className="relative">
                  <Mail size={15} className="absolute left-4 top-1/2 -translate-y-1/2 text-[#A1A1AA] pointer-events-none" />
                  <input
                    data-testid="delete-email-input"
                    type="email"
                    value={form.email}
                    onChange={set("email")}
                    placeholder="The email linked to your Fanible account"
                    className="w-full bg-white/[0.05] border border-white/10 rounded-xl pl-10 pr-4 py-3.5 text-white text-sm font-inter placeholder-[#555] outline-none focus:border-red-400/50 transition-all"
                  />
                </div>
              </div>

              <div>
                <label className="block text-[10px] font-bold uppercase tracking-widest text-[#A1A1AA] mb-2">
                  Username <span className="text-[#555]">(optional)</span>
                </label>
                <div className="relative">
                  <User size={15} className="absolute left-4 top-1/2 -translate-y-1/2 text-[#A1A1AA] pointer-events-none" />
                  <input
                    data-testid="delete-username-input"
                    type="text"
                    value={form.username}
                    onChange={set("username")}
                    placeholder="Your Fanible username"
                    className="w-full bg-white/[0.05] border border-white/10 rounded-xl pl-10 pr-4 py-3.5 text-white text-sm font-inter placeholder-[#555] outline-none focus:border-red-400/50 transition-all"
                  />
                </div>
              </div>

              <div>
                <label className="block text-[10px] font-bold uppercase tracking-widest text-[#A1A1AA] mb-2">
                  Reason for Deletion <span className="text-red-400">*</span>
                </label>
                <div className="relative">
                  <select
                    data-testid="delete-reason-select"
                    value={form.reason}
                    onChange={set("reason")}
                    className="w-full appearance-none bg-white/[0.05] border border-white/10 rounded-xl px-4 pr-10 py-3.5 text-sm font-inter outline-none focus:border-red-400/50 transition-all cursor-pointer"
                    style={{ color: form.reason ? "#fff" : "#555" }}
                  >
                    <option value="" disabled className="bg-[#111420]">Select a reason</option>
                    {REASONS.map((r) => (
                      <option key={r} value={r} className="bg-[#111420] text-white">{r}</option>
                    ))}
                  </select>
                  <ChevronDown size={15} className="absolute right-4 top-1/2 -translate-y-1/2 text-[#A1A1AA] pointer-events-none" />
                </div>
              </div>

              <div>
                <label className="block text-[10px] font-bold uppercase tracking-widest text-[#A1A1AA] mb-2">
                  Additional Details <span className="text-[#555]">(optional)</span>
                </label>
                <textarea
                  data-testid="delete-details-input"
                  value={form.details}
                  onChange={set("details")}
                  rows={3}
                  placeholder="Anything else we should know..."
                  className="w-full bg-white/[0.05] border border-white/10 rounded-xl px-4 py-3.5 text-white text-sm font-inter placeholder-[#555] outline-none focus:border-red-400/50 transition-all resize-none"
                />
              </div>

              {error && (
                <p data-testid="delete-error" className="text-red-400 text-xs font-inter px-1">{error}</p>
              )}

              <button
                data-testid="delete-submit-btn"
                type="submit"
                disabled={loading}
                className="w-full flex items-center justify-center gap-2 bg-red-500 text-white font-bold text-sm uppercase tracking-wider rounded-xl py-4 hover:bg-red-400 transition-colors duration-150 disabled:opacity-50"
              >
                {loading ? (
                  <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24" fill="none">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                  </svg>
                ) : (
                  <><Trash2 size={15} /> Submit Deletion Request</>
                )}
              </button>
            </form>

            {/* Alternatives */}
            <div className="mt-8 bg-white/[0.02] border border-white/8 rounded-2xl p-5 space-y-2">
              <p className="text-white font-semibold text-sm">Other ways to delete your account</p>
              <p className="text-[#A1A1AA] text-xs font-inter leading-relaxed">
                In-app: <span className="text-white">Settings → Account → Delete Account</span>
              </p>
              <p className="text-[#A1A1AA] text-xs font-inter leading-relaxed">
                Email: <a href="mailto:hello@fanible.com" className="text-[#3B9EFF] hover:underline">hello@fanible.com</a>
              </p>
            </div>
          </>
        ) : (
          /* Success State */
          <div data-testid="delete-success" className="bg-white/[0.02] border border-white/8 rounded-2xl p-10 text-center">
            <div className="w-16 h-16 rounded-full bg-[#3B9EFF]/10 border border-[#3B9EFF]/25 flex items-center justify-center mx-auto mb-6">
              <CheckCircle size={32} className="text-[#3B9EFF]" />
            </div>
            <h2 className="font-outfit font-black text-white text-2xl mb-3">
              {alreadyExists ? "Request Already Pending" : "Request Received"}
            </h2>
            <p className="text-[#A1A1AA] text-sm font-inter leading-relaxed max-w-md mx-auto mb-6">
              {alreadyExists
                ? `A deletion request for ${form.email} is already pending. You do not need to submit again.`
                : `We've received your deletion request for ${form.email}. Our team will verify it and initiate deletion within 24 hours.`}
            </p>
            <div className="flex items-center justify-center gap-2 text-[#A1A1AA] text-xs font-inter">
              <Clock size={13} className="text-[#3B9EFF]" />
              <span>Processing time: within 24 hours after verification</span>
            </div>
            <a
              href="/"
              className="inline-flex items-center gap-2 mt-8 text-sm text-[#3B9EFF] hover:text-white transition-colors font-semibold"
            >
              <ArrowLeft size={14} /> Back to Fanible
            </a>
          </div>
        )}

        {/* Footer */}
        <div className="pt-8 mt-4 border-t border-white/8 flex items-center justify-between">
          <a href="/" className="text-sm text-[#A1A1AA] hover:text-[#3B9EFF] transition-colors font-inter">← Back to Fanible</a>
          <p className="text-[#A1A1AA] text-xs font-inter">© 2026 Fanible Private Limited</p>
        </div>

      </div>
    </div>
  );
}
